import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import HubspotForm from './HubSpotForm';
import HeroVisual from './HeroVisual';
import ScrollReveal from './ScrollReveal';

function Home() {
    return (
        <main>
            {/* ===== HERO ===== */}
            <section className="site-section-lg">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={10} xl={9}>
                            <ScrollReveal animation="fade-in">
                                <span className="badge-ea mb-4 d-inline-block">Research Preview &middot; Pre-release</span>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <h1 className="hero-headline mb-4">
                                    Stop disinformation at the citation.
                                </h1>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={250}>
                                <p className="hero-sub mx-auto mb-4">
                                    Every reference checked. Every quote traced. Every score you can audit.
                                    Referom checks the citations in academic work to make sure they actually
                                    hold up &mdash; built for universities, schools, scholarly journals, and
                                    conferences dealing with submissions where the citations are hard to
                                    verify.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <div className="d-flex gap-3 justify-content-center flex-wrap">
                                    <a href="#contact" className="btn-primary-cta">Join the Research Partner Program</a>
                                    <a href="#how" className="btn-secondary-cta">How It Works</a>
                                </div>
                            </ScrollReveal>

                            {/* --- Value Prop Indicators --- */}
                            <ScrollReveal animation="fade-up" delay={550}>
                                <div className="hero-value-row">
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M9 12l2 2 4-4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Per-Citation Scoring</p>
                                            <p className="hero-value-desc">Each citation gets a 0&ndash;100 integrity score. No black-box paper-level verdicts.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Source Traceback</p>
                                            <p className="hero-value-desc">Every claim tied to the actual sentence in the actual reference document.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="9" y1="15" x2="15" y2="15" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Reproducible Audits</p>
                                            <p className="hero-value-desc">Every score is stamped with the version of the system that produced it. Re-runs add to the record &mdash; they don&rsquo;t overwrite it.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Strict Separation</p>
                                            <p className="hero-value-desc">Each institution&rsquo;s submissions stay private. Nothing leaks between organizations.</p>
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>

                            <HeroVisual />
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== THE PROBLEM ===== */}
            <section id="why" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-4">
                                    <span className="eyebrow-lead">Why Referom Exists</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    Plagiarism detection isn&rsquo;t enough anymore.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3 lead-paragraph">
                                    There&rsquo;s a lot of bad information out there, and corrections come too late to
                                    help. Citations and quotes in scholarly work are hard to verify quickly.
                                    Generative AI invents plausible-looking sources at scale, and the fixes show
                                    up months after the damage is done.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    Plagiarism tools cover one part of the problem &mdash; text overlap. No one is
                                    really focused on <em>referential integrity</em>: checking that a citation points to
                                    a real source, that the source actually says what the paper claims it says, and
                                    that you can score and audit the whole thing.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    Editors and reviewers can&rsquo;t keep up with submission volume. Program committees
                                    can&rsquo;t sort through fabricated bibliographies fast enough. Research offices
                                    can&rsquo;t check every grant deliverable end to end. The bottleneck was never
                                    finding duplicated prose &mdash; it&rsquo;s confirming that the references behind the
                                    prose are real and accurately described.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== THE SOLUTION ===== */}
            <section id="solution" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Our Approach</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    Referential integrity, not just text overlap.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    Referom pulls the citations out of a paper, finds the original source for each
                                    one, and checks how well the citation holds up &mdash; does the source exist?
                                    does it actually support the claim? is the quote accurate? &mdash; then produces
                                    a score you can audit.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    Every check is logged, so the result is reproducible. Re-run the analysis with a
                                    newer system or a larger source library and you get a new score &mdash; the old
                                    scores stay on the record, stamped with the exact version of the system that
                                    produced them.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    The web app is a temporary front door. Long-term, Referom plugs into the tools
                                    your institution already uses &mdash; submission systems, project management,
                                    learning-management gradebooks &mdash; so integrity scores and source links show
                                    up where your team already works, without anyone having to log into a new product.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== PHASE 1 AUDIENCES ===== */}
            <section id="audiences" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Who Phase 1 Serves</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    Four academic audiences. One value: speed to quality.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading">
                                    Across all four Phase&nbsp;1 audiences the value is the same &mdash; quickly
                                    surface which citations look questionable, with enough detail that an editor,
                                    reviewer, or research office knows exactly where to focus. Not a yes/no
                                    verdict on the paper. Not a writing grade. A short path from submission to
                                    something you can act on.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4 mt-2">
                        <Col md={6} lg={6} xl={5}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="d-flex align-items-baseline gap-3 mb-2">
                                        <span className="audience-rank">01</span>
                                        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, margin: 0, color: 'var(--rf-ink)' }}>
                                            Universities
                                        </h3>
                                    </div>
                                    <p className="audience-meta">Research offices &middot; Deans of research &middot; Graduate schools &middot; Departmental chairs &middot; Writing-center librarians</p>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        Cut the time from thesis, dissertation, or grant-deliverable submission to a
                                        per-citation integrity score you can act on. Show exactly which citations
                                        warrant a closer look &mdash; without your team reading every bibliography
                                        end to end.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={6} xl={5}>
                            <ScrollReveal animation="fade-up" delay={100} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="d-flex align-items-baseline gap-3 mb-2">
                                        <span className="audience-rank">02</span>
                                        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, margin: 0, color: 'var(--rf-ink)' }}>
                                            High schools
                                        </h3>
                                    </div>
                                    <p className="audience-meta">School and district administrators &middot; Curriculum offices &middot; Learning-platform owners</p>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        Higher unit volume than universities, in the segment where AI-assisted
                                        student writing is making citation problems worse the fastest.
                                        Per-citation flags &mdash; fabricated, misattributed, or unverifiable
                                        &mdash; delivered into the platforms classrooms already use.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={6} xl={5}>
                            <ScrollReveal animation="fade-up" delay={200} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="d-flex align-items-baseline gap-3 mb-2">
                                        <span className="audience-rank">03</span>
                                        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, margin: 0, color: 'var(--rf-ink)' }}>
                                            Scholarly journals
                                        </h3>
                                    </div>
                                    <p className="audience-meta">Editorial offices &middot; Managing editors &middot; Peer-review coordinators &middot; Manuscript-tracking-system owners</p>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        Shorten the editorial cycle on submissions where the bibliography is hard
                                        to verify. Pre-screen for fabricated or misattributed citations before
                                        you commit a reviewer&rsquo;s time.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={6} xl={5}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="d-flex align-items-baseline gap-3 mb-2">
                                        <span className="audience-rank">04</span>
                                        <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, margin: 0, color: 'var(--rf-ink)' }}>
                                            Conferences
                                        </h3>
                                    </div>
                                    <p className="audience-meta">Program committee chairs &middot; Area chairs &middot; Conference organizers running tight peer-review cycles</p>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        Speed to quality on seasonal review cycles where reviewers are stretched
                                        thin and submission volume spikes. Sort through fabricated bibliographies
                                        fast, inside your review window.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center mt-4">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <p className="text-dim text-center" style={{ fontSize: '0.92rem', fontStyle: 'italic', margin: 0 }}>
                                    This is the default order &mdash; but if a journal or conference is ready to
                                    go first, we&rsquo;ll start there.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== INTEGRATION / API-FIRST ===== */}
            <section id="integration" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Integration Posture</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-4 text-center">
                                    API-first by design. Your workflow doesn&rsquo;t move &mdash; Referom plugs into it.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    The web interface is just one way to use Referom. Everything also runs through
                                    an API, so it can plug into the tools you already have. Universities plug it
                                    into their LMS or research-information system. Schools call it from the
                                    platforms they already run in their districts and classrooms. Journals trigger
                                    it from their manuscript-tracking system. Conferences run it through their
                                    peer-review platform.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    The integrity score, the per-citation detail, and the link back to the original
                                    source all show up in the tool your team already uses &mdash; not in a new
                                    product they have to learn, log into, and maintain. The value lands inside the
                                    workflow that&rsquo;s already in place.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    We&rsquo;ve built this kind of integration before &mdash; scoring engines that
                                    plug into the systems customers already run &mdash; so onboarding stays
                                    manageable and you see value quickly.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== THREE PILLARS ===== */}
            <section id="pillars" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">What You Get</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    Three guarantees on every analysis.
                                </h2>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M9 12l2 2 4-4" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Per-Citation Integrity Score
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Every citation gets its own 0&ndash;100 score, with sub-scores for whether
                                        the source supports the claim, whether the paraphrase is faithful, and
                                        whether the right paper was attributed. Click into any low score to see
                                        why &mdash; no source found, content doesn&rsquo;t match, paraphrase has
                                        drifted, wrong paper cited.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Source Traceback
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Every claim links to the exact sentence in the actual source document
                                        &mdash; not just a DOI. If twenty manuscripts cite the same paper, we look
                                        it up once, not twenty times.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="9" y1="15" x2="15" y2="15" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Reproducible Audit
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Every integrity check is stamped with the version of the system that
                                        produced it. The same paper, run through the same version, gets the same
                                        score. Re-runs add new entries; old ones stay on the record. Nothing
                                        rewrites history.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== WHAT REFEROM IS NOT ===== */}
            <section id="scope" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Scope, Stated Plainly</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    What Referom is not.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading">
                                    Two nearby product categories are out of scope on purpose &mdash; not by
                                    neglect and not because we&rsquo;ll add them later. The contrast matters
                                    because the underlying problem keeps getting mistaken for one of these two.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={6}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100 scope-not-card">
                                    <span className="scope-not-label">Not</span>
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.85rem', marginTop: '0.5rem', color: 'var(--rf-ink)' }}>
                                        A writing-style critique tool.
                                    </h3>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        We don&rsquo;t judge whether the writing is well-written, scholarly in
                                        tone, or right for a particular venue. Plenty of tools already do that
                                        well. It&rsquo;s also a different problem from the one we&rsquo;re built
                                        to solve.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100 scope-not-card">
                                    <span className="scope-not-label">Not</span>
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.85rem', marginTop: '0.5rem', color: 'var(--rf-ink)' }}>
                                        A plagiarism rating.
                                    </h3>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        We don&rsquo;t check text overlap against prior work. Plagiarism detection
                                        is a well-served space with strong existing tools. We don&rsquo;t compete
                                        with them, don&rsquo;t benchmark against them, and don&rsquo;t list it as
                                        a feature.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center mt-4">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <div className="scope-contrast">
                                    <p style={{ fontSize: '1.02rem', lineHeight: 1.75, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        <strong style={{ color: 'var(--rf-ink)' }}>What Referom <em>does</em> address:</strong>{' '}
                                        generative AI is producing huge amounts of new content &mdash; academic
                                        and otherwise. A lot of people are hoping the sheer volume hides the
                                        underlying problem: that the integrity of citations and references has
                                        been quietly falling apart inside that flood. Plagiarism tools weren&rsquo;t
                                        built to catch it. Style checkers can&rsquo;t see it. Referom is built
                                        specifically for that gap.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== HOW IT WORKS — METHOD AS PRINCIPLES ===== */}
            <section id="how" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-5">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">Our Method</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    Three commitments before any line of code.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading">
                                    Referom is a position before it&rsquo;s a piece of software. The mechanics
                                    will keep changing; these three commitments won&rsquo;t.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.85rem', color: 'var(--rf-ink)' }}>
                                        Integrity is measured, not asserted.
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        The only honest answer to <em>&ldquo;is this citation trustworthy?&rdquo;</em>
                                        is one that can be reproduced and audited. We score each citation against
                                        the actual evidence &mdash; not against a guess about what looks plausible.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.85rem', color: 'var(--rf-ink)' }}>
                                        The claim must reach the sentence.
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        A citation is a claim about a specific passage in a specific source. The
                                        check isn&rsquo;t done until we&rsquo;ve found that passage and compared
                                        the claim to what the source actually says &mdash; in its own words.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.85rem', color: 'var(--rf-ink)' }}>
                                        Provenance is part of the score.
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Every check is stamped with the method that produced it. Re-running with
                                        newer evidence produces a new score; the old one stays on the record,
                                        unchanged. Nothing rewrites history. The audit trail itself is what you
                                        get.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== PHASE 1 → PHASE 2 VISION ===== */}
            <section id="vision" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <div className="text-center mb-3">
                                    <span className="eyebrow-quiet">The Long Arc</span>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={50}>
                                <h2 className="section-heading mb-3">
                                    Built for scholarship. Documented for what comes next.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading">
                                    Phase 1 is the work right now. We&rsquo;re documenting Phase 2 openly so the
                                    choices we make today don&rsquo;t quietly box us into something else &mdash;
                                    and so academic partners know exactly where we&rsquo;re headed long-term.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col lg={6}>
                            <ScrollReveal animation="fade-right" delay={0} className="h-100">
                                <div className="glass-card-lg h-100">
                                    <span className="badge-ea mb-3 d-inline-block">Phase 1 &middot; The Work</span>
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.4rem', fontWeight: 600, marginBottom: '0.85rem', color: 'var(--rf-ink)' }}>
                                        Scholarly publishing &amp; academia.
                                    </h3>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.75, marginBottom: '1rem', color: 'var(--rf-ink-soft)' }}>
                                        Universities, secondary schools, scholarly journals, and conferences are
                                        receiving submissions whose citations are made up, taken out of context, or
                                        wholly fabricated. Editors and reviewers can&rsquo;t keep up. Plagiarism
                                        tools only check for text overlap &mdash; not whether the references
                                        actually hold up.
                                    </p>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.75, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        The problem is sharp, and the input is well-shaped (manuscripts and student
                                        work with bibliographies). The sources are checkable against DOI registries,
                                        OpenAlex, arXiv, and institutional repositories. If we can&rsquo;t earn
                                        trust here &mdash; where the problem is clearest and the sources are easiest
                                        to verify &mdash; we haven&rsquo;t earned the right to ask anyone else to
                                        trust our scores.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col lg={6}>
                            <ScrollReveal animation="fade-left" delay={150} className="h-100">
                                <div className="glass-card-lg h-100">
                                    <span className="eyebrow-quiet mb-3 d-inline-block">Phase 2 &middot; After PMF</span>
                                    <h3 style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.4rem', fontWeight: 600, marginBottom: '0.85rem', color: 'var(--rf-ink)' }}>
                                        Documented, not yet attempted.
                                    </h3>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.75, marginBottom: '1rem', color: 'var(--rf-ink-soft)' }}>
                                        The same scoring engine &mdash; <em>does the cited claim trace back to a real
                                        source that actually says what&rsquo;s claimed?</em> &mdash; could one day
                                        apply to news articles, op-eds, and longer-form public discussion.
                                    </p>
                                    <p style={{ fontSize: '0.95rem', lineHeight: 1.75, margin: 0, color: 'var(--rf-ink-soft)' }}>
                                        We&rsquo;re open about this so academic partners know where we&rsquo;re
                                        headed long-term. We&rsquo;re not building it yet. We won&rsquo;t start until
                                        the scholarly use case is in production and trusted. And the reader, not
                                        us, would always control the filter. We don&rsquo;t want to be content
                                        moderators.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== FOUNDER ===== */}
            <section id="founder" className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-2">Principal Investigator</h2>
                                <p className="section-subheading">
                                    Generative AI is producing more plausible-looking content &mdash; academic and
                                    otherwise &mdash; than scholars, editors, and reviewers can keep up with.
                                    It&rsquo;s getting harder to tell what&rsquo;s real, what&rsquo;s worth
                                    reading, and what&rsquo;s worth citing or sharing. Referom is built
                                    specifically for that problem.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <ScrollReveal animation="scale-in">
                                <div className="glass-card d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4 text-center text-md-start">
                                    <img
                                        src="/team/andrew_schwabe.jpg"
                                        alt="Andrew Schwabe"
                                        className="founder-photo"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    <div>
                                        <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-2 mb-1">
                                            <p className="founder-name">Andrew Schwabe</p>
                                            <a href="https://www.linkedin.com/in/aschwabe/" target="_blank" rel="noopener noreferrer" className="linkedin-icon" aria-label="LinkedIn">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
                                                </svg>
                                            </a>
                                        </div>
                                        <p className="founder-title mb-2">Founder &amp; Author</p>
                                        <p className="founder-credentials mb-3">
                                            <a
                                                href="https://research-portal.st-andrews.ac.uk/en/persons/andrew-schwabe/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                                                    <path d="M6 12v5c3 3 9 3 12 0v-5" />
                                                </svg>
                                                <span>Research Portal &middot; University of St&nbsp;Andrews</span>
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="ext-icon">
                                                    <path d="M7 17L17 7M9 7h8v8" />
                                                </svg>
                                            </a>
                                        </p>
                                        <p className="founder-bio">
                                            Serial entrepreneur. Founded ForMotiv, Saigon AI, and QCoda. Building
                                            Referom because generative AI is flooding scholarship &mdash; and
                                            everywhere else &mdash; with content the world can&rsquo;t keep up with.
                                            <em>What&rsquo;s real, what&rsquo;s worth reading, and what&rsquo;s worth
                                            citing</em> deserves a real answer you can actually check. Plagiarism
                                            tools weren&rsquo;t built for that. Style checkers can&rsquo;t see it.
                                            Referom is.
                                        </p>
                                    </div>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== FINAL CTA / CONTACT ===== */}
            <section className="site-section-lg" id="contact">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={7} xl={6}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-3">Join the Research Partner Program.</h2>
                                <p className="section-subheading mb-4">
                                    A small group of universities, schools, journals, and conferences is helping
                                    shape v1 before we open it up more widely. If your team is dealing with
                                    submissions where the citations are hard to verify, we&rsquo;d like to hear
                                    from you.
                                </p>
                                <div className="form-waitlist mb-4 mx-auto" style={{ maxWidth: '480px' }}>
                                    <HubspotForm
                                        portalId="244482894"
                                        formId="6b2f65aa-22f4-4496-b70c-73a27d56d911"
                                        pageName="Referom Research Partner"
                                        submitLabel="Request Info"
                                        successMessage="Thank you — we will be in touch."
                                        fields={[
                                            { name: 'email', label: 'Institutional email', type: 'email', required: true, placeholder: 'you@institution.edu' },
                                        ]}
                                    />
                                </div>
                                <p className="text-dim" style={{ fontSize: '0.9rem' }}>
                                    Or write to <a href="mailto:hello@referom.com" style={{ color: 'var(--rf-navy)', textDecoration: 'underline' }}>hello@referom.com</a> for a direct introduction.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default Home;
